import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";

import { WFPButton } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

import { disputeDelivery } from "../../deliveryConfirmationApi";
import { responseError } from "../DeliveryCostNegotiationModal";
import { Offer } from "../OfferCard/interfaces";
import {
  createDisputeDeliverySchema,
  DISPUTE_TOPICS,
  type DisputeDeliveryValues,
  MAX_DISPUTE_COMMENT_LENGTH,
} from "./schema";

interface DisputeDeliveryModalProps {
  offer: Offer;
  onCancel: () => void;
  onDisputed: () => void;
}

const DEFAULT_VALUES: DisputeDeliveryValues = { topics: [], comment: "" };

export const DisputeDeliveryModal = ({
  offer,
  onCancel,
  onDisputed,
}: DisputeDeliveryModalProps) => {
  const { t } = useTranslation();
  const [actionError, setActionError] = useState("");

  const resolver = useMemo(() => zodResolver(createDisputeDeliverySchema(t)), [t]);
  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<DisputeDeliveryValues>({ resolver, defaultValues: DEFAULT_VALUES });

  const onSubmit = handleSubmit(async values => {
    setActionError("");
    try {
      await disputeDelivery(offer.id, values.topics, values.comment.trim());
      onDisputed();
    } catch (error) {
      setActionError(responseError(error, t("disputeDeliveryModal.error")));
    }
  });

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("disputeDeliveryModal.title")}</DialogTitle>
          <DialogDescription>
            {t("disputeDeliveryModal.description", { aggregator: offer.tagged_aggregator?.name })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 font-semibold text-foreground text-sm">
              {`${t("disputeDeliveryModal.topics")} *`}
            </legend>
            <Controller
              control={control}
              name="topics"
              render={({ field }) => (
                <>
                  {DISPUTE_TOPICS.map(topic => (
                    <label key={topic} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={field.value.includes(topic)}
                        onCheckedChange={checked =>
                          field.onChange(
                            checked === true
                              ? [...field.value, topic]
                              : field.value.filter(value => value !== topic)
                          )
                        }
                      />
                      {t(`deliveryTopics.${topic}`)}
                    </label>
                  ))}
                </>
              )}
            />
            {errors.topics && (
              <p role="alert" className="text-destructive text-sm">
                {errors.topics.message}
              </p>
            )}
          </fieldset>

          <div className="flex flex-col gap-2">
            <Label htmlFor="dispute_comment" className="font-semibold text-foreground">
              {`${t("disputeDeliveryModal.comment")} *`}
            </Label>
            <Textarea
              id="dispute_comment"
              maxLength={MAX_DISPUTE_COMMENT_LENGTH}
              placeholder={t("disputeDeliveryModal.commentPlaceholder")}
              aria-invalid={!!errors.comment}
              {...register("comment")}
            />
            {errors.comment && (
              <p role="alert" className="text-destructive text-sm">
                {errors.comment.message}
              </p>
            )}
          </div>

          {actionError && (
            <p role="alert" className="text-destructive text-sm">
              {actionError}
            </p>
          )}

          <DialogFooter>
            <WFPButton type="button" variant="secondary" onClick={onCancel}>
              {t("common.cancel")}
            </WFPButton>
            <WFPButton type="submit" disabled={isSubmitting}>
              {isSubmitting && <Spinner className="size-4" />}
              {t("disputeDeliveryModal.submit")}
            </WFPButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
